import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Brain, Activity } from 'lucide-react';
import './HeroDemo.css';

// ─── Mock data ───────────────────────────────────────────────────────────────

const RACES = [
  {
    time: '14:30',
    course: 'Newmarket',
    horse: 'Thunder Bay',
    confidence: 'HIGH',
    odds: '4.50',
    stake: '1.0',
    betAmt: '£10.00',
    scores: { overall: 82, horse: 78, jockey: 85, trainer: 80, owner: 72 },
    analysis:
      'Strong course form at Newmarket. Jockey booking upgrade signals trainer confidence. Soft ground suits well.',
  },
  {
    time: '15:00',
    course: 'Ascot',
    horse: 'Silver Star',
    confidence: 'MEDIUM',
    odds: '6.00',
    stake: '0.5',
    betAmt: '£5.00',
    scores: { overall: 71, horse: 74, jockey: 68, trainer: 75, owner: 65 },
    analysis: 'Consistent on good ground. Trainer in form with similar profiles recently.',
  },
  {
    time: '15:45',
    course: 'Haydock',
    horse: 'Royal Quest',
    confidence: 'HIGH',
    odds: '3.20',
    stake: '1.5',
    betAmt: '£15.00',
    scores: { overall: 88, horse: 85, jockey: 90, trainer: 82, owner: 79 },
    analysis: 'Top-rated in field. Dominant recent form over this distance and going.',
  },
];

const RESULTS = [
  { horse: 'Thunder Bay', odds: '4.50', result: 'WON' as const, pnl: '+£35.00' },
  { horse: 'Royal Quest', odds: '3.20', result: 'WON' as const, pnl: '+£33.00' },
  { horse: 'Silver Star', odds: '6.00', result: 'LOST' as const, pnl: '-£5.00' },
  { horse: 'Morning Dew', odds: '5.50', result: 'WON' as const, pnl: '+£22.50' },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

const ScoreRing: React.FC<{ score: number; label: string; delay?: number }> = ({
  score,
  label,
  delay = 0,
}) => {
  const r = 13;
  const circ = 2 * Math.PI * r;
  const color = score >= 80 ? '#22c55e' : score >= 65 ? '#f59e0b' : '#ef4444';
  return (
    <div className="demo-score-ring">
      <svg width="34" height="34" viewBox="0 0 34 34">
        <circle cx="17" cy="17" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
        <motion.circle
          cx="17"
          cy="17"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ * (1 - score / 100) }}
          transition={{ duration: 1.0, delay, ease: 'easeOut' }}
          style={{ transform: 'rotate(-90deg)', transformOrigin: '17px 17px' }}
        />
        <text x="17" y="21.5" textAnchor="middle" fontSize="8" fill={color} fontWeight="700">
          {score}
        </text>
      </svg>
      <span className="demo-score-label">{label}</span>
    </div>
  );
};

const CountUp: React.FC<{
  to: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}> = ({ to, prefix = '', suffix = '', decimals = 0 }) => {
  const [val, setVal] = useState(0);

  useEffect(() => {
    let raf: number;
    let start: number | null = null;
    const duration = 1200;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(parseFloat((eased * to).toFixed(decimals)));
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      } else {
        setVal(to);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, decimals]);

  return (
    <>
      {prefix}
      {val.toFixed(decimals)}
      {suffix}
    </>
  );
};

const Sparkline: React.FC = () => {
  const data = [0, 15, 10, 28, 22, 40, 48, 44, 62, 72, 68, 85, 95, 88, 105, 120];
  const W = 260;
  const H = 60;
  const max = Math.max(...data);
  const toX = (i: number) => (i / (data.length - 1)) * W;
  const toY = (v: number) => H - (v / max) * (H - 8) - 4;
  const lineD = data
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(v).toFixed(1)}`)
    .join(' ');
  const areaD = `${lineD} L ${W} ${H} L 0 ${H} Z`;

  return (
    <div className="demo-sparkline">
      <div className="demo-sparkline-label">P&L trend · last 16 days</div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="demo-sparkline-svg"
      >
        <motion.path
          d={areaD}
          fill="rgba(34,197,94,0.08)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
        />
        <motion.path
          d={lineD}
          fill="none"
          stroke="#22c55e"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.6, ease: 'easeInOut' }}
        />
      </svg>
      <div className="demo-sparkline-axis">
        <span>Apr 14</span>
        <span>Apr 21</span>
        <span>Apr 29</span>
      </div>
    </div>
  );
};

// ─── Animation variants ──────────────────────────────────────────────────────

const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

// ─── Tab panels ──────────────────────────────────────────────────────────────

const PicksPanel: React.FC = () => (
  <motion.div
    className="demo-panel"
    variants={listVariants}
    initial="hidden"
    animate="show"
  >
    <motion.div className="demo-panel-header" variants={itemVariants}>
      <Brain size={13} strokeWidth={2} />
      <span>Today · 3 picks found</span>
      <span className="demo-date-chip">Wed 29 Apr</span>
    </motion.div>

    <div className="demo-race-list">
      {RACES.map((race, i) => (
        <motion.div
          key={race.horse}
          className={`demo-race-row${i === 0 ? ' demo-race-row--active' : ''}`}
          variants={itemVariants}
        >
          <div className="demo-race-row-left">
            <span className="demo-time">{race.time}</span>
            <span className="demo-course">{race.course}</span>
            <span className="demo-horse">{race.horse}</span>
          </div>
          <div className="demo-race-row-right">
            <span className={`demo-conf demo-conf--${race.confidence.toLowerCase()}`}>
              {race.confidence}
            </span>
            <span className="demo-odds">{race.odds}</span>
            <span className="demo-stake">{race.stake}pt</span>
          </div>
        </motion.div>
      ))}
    </div>

    <motion.div className="demo-expanded" variants={itemVariants}>
      <div className="demo-scores-row">
        {(
          [
            { label: 'Overall', val: RACES[0].scores.overall },
            { label: 'Horse', val: RACES[0].scores.horse },
            { label: 'Jockey', val: RACES[0].scores.jockey },
            { label: 'Trainer', val: RACES[0].scores.trainer },
            { label: 'Owner', val: RACES[0].scores.owner },
          ] as const
        ).map(({ label, val }, i) => (
          <ScoreRing key={label} score={val} label={label} delay={0.4 + i * 0.08} />
        ))}
      </div>
      <p className="demo-analysis">{RACES[0].analysis}</p>
    </motion.div>
  </motion.div>
);

const AutoBetPanel: React.FC = () => (
  <motion.div
    className="demo-panel"
    variants={listVariants}
    initial="hidden"
    animate="show"
  >
    <motion.div className="demo-panel-header demo-panel-header--ai" variants={itemVariants}>
      <Bot size={13} strokeWidth={2} />
      <span>AI Mode</span>
      <span className="demo-ai-dot" />
      <span className="demo-ai-status">Active</span>
    </motion.div>

    <motion.p className="demo-ai-subtitle" variants={itemVariants}>
      Bets placed automatically from 11:00
    </motion.p>

    <div className="demo-bet-list">
      {RACES.map((race, i) => (
        <motion.div key={race.horse} className="demo-bet-row" variants={itemVariants}>
          <div className="demo-bet-info">
            <span className="demo-time">{race.time}</span>
            <span className="demo-course">{race.course}</span>
            <span className="demo-horse">{race.horse}</span>
          </div>
          <div className="demo-bet-row-right">
            <span className="demo-betfair-chip">Betfair</span>
            <span className="demo-bet-amount">
              {race.betAmt} @ {race.odds}
            </span>
            <motion.span
              className="demo-placed-chip"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.5 + i * 0.45,
                type: 'spring',
                stiffness: 280,
                damping: 20,
              }}
            >
              ✓ Placed
            </motion.span>
          </div>
        </motion.div>
      ))}
    </div>

    <motion.div className="demo-bet-total" variants={itemVariants}>
      3 bets · £30.00 staked today
    </motion.div>
  </motion.div>
);

const ResultsPanel: React.FC = () => (
  <motion.div
    className="demo-panel"
    variants={listVariants}
    initial="hidden"
    animate="show"
  >
    <motion.div className="demo-panel-header" variants={itemVariants}>
      <Activity size={13} strokeWidth={2} />
      <span>Performance</span>
    </motion.div>

    <motion.div className="demo-stats-row" variants={itemVariants}>
      {[
        { label: 'Win Rate', to: 68, suffix: '%', decimals: 0 },
        { label: 'P&L', to: 247, prefix: '+£', decimals: 0 },
        { label: 'ROI', to: 12.3, prefix: '+', suffix: '%', decimals: 1 },
      ].map(({ label, to, prefix, suffix, decimals }) => (
        <div key={label} className="demo-stat-card">
          <div className="demo-stat-value">
            <CountUp to={to} prefix={prefix} suffix={suffix} decimals={decimals} />
          </div>
          <div className="demo-stat-label">{label}</div>
        </div>
      ))}
    </motion.div>

    <motion.div variants={itemVariants}>
      <Sparkline />
    </motion.div>

    <motion.div className="demo-results-list" variants={listVariants}>
      {RESULTS.map((r) => (
        <motion.div key={r.horse} className="demo-result-row" variants={itemVariants}>
          <span className={`demo-result-icon demo-result-icon--${r.result.toLowerCase()}`}>
            {r.result === 'WON' ? '✓' : '✗'}
          </span>
          <span className="demo-horse">{r.horse}</span>
          <span className="demo-odds-sm">{r.odds}</span>
          <span className={`demo-pnl demo-pnl--${r.result.toLowerCase()}`}>{r.pnl}</span>
        </motion.div>
      ))}
    </motion.div>
  </motion.div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const TABS = [
  { key: 'picks', label: "Today's Picks", shortLabel: 'Picks', Icon: Brain },
  { key: 'autobet', label: 'AI Auto-Bet', shortLabel: 'Auto-Bet', Icon: Bot },
  { key: 'results', label: 'Track Results', shortLabel: 'Results', Icon: Activity },
] as const;

const PANELS = [PicksPanel, AutoBetPanel, ResultsPanel];

const HeroDemo: React.FC = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => {
      setActive((prev) => (prev + 1) % 3);
    }, 3500);
    return () => clearTimeout(t);
  }, [active, paused]);

  const Panel = PANELS[active];

  return (
    <div
      className="hero-demo"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Frame chrome — browser bar on desktop, pill on mobile */}
      <div className="hero-demo-chrome">
        <div className="hero-demo-dots">
          <span className="hero-demo-dot hero-demo-dot--red" />
          <span className="hero-demo-dot hero-demo-dot--yellow" />
          <span className="hero-demo-dot hero-demo-dot--green" />
        </div>
        <span className="hero-demo-url">gallopai.app</span>
        <span className="hero-demo-chrome-spacer" aria-hidden />
      </div>

      {/* Tab bar */}
      <div className="hero-demo-tabs" role="tablist">
        {TABS.map((tab, i) => {
          const Icon = tab.Icon;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={i === active}
              className={`hero-demo-tab${i === active ? ' hero-demo-tab--active' : ''}`}
              onClick={() => {
                setActive(i);
                setPaused(true);
              }}
            >
              <Icon size={11} strokeWidth={2} />
              <span className="hero-demo-tab-label">{tab.label}</span>
              <span className="hero-demo-tab-label-short">{tab.shortLabel}</span>
            </button>
          );
        })}
      </div>

      {/* Auto-advance progress bar */}
      <div className="hero-demo-progress-track" aria-hidden>
        {!paused && (
          <motion.div
            key={active}
            className="hero-demo-progress-fill"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 3.5, ease: 'linear' }}
          />
        )}
      </div>

      {/* Animated panel content */}
      <div className="hero-demo-body">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            className="hero-demo-panel-wrap"
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -14 }}
            transition={{ duration: 0.28 }}
          >
            <Panel />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HeroDemo;
